import SearchIcon from '@mui/icons-material/Search'
import {
  Autocomplete,
  InputAdornment,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import { FC, useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useSearchResultsVehicles } from '../../hooks'
import { AppRoutes } from '../../models'
import { VehicleEntry } from '../../sdk'
import { describeLicense, describeVehicle } from '../../utils'
import { ListItemLoading } from '../SearchWithAutocomplete/ListItemLoading'
import { NoOptionsText } from '../SearchWithAutocomplete/NoOptionsText'
import { VehicleListItem } from '../SearchWithAutocomplete/VehicleListItem'
import { ClickableDeletableCard } from '../SharedUI'

interface Props {
  vehicle: VehicleEntry | undefined
  handleVehicleChange: (vehicle: VehicleEntry | undefined) => void
  error?: boolean
  readOnly?: boolean
  isClickable?: boolean
  autoFocus?: boolean
}

// TODO Bug: no option text is never displayed as create new vehicle button is considered as one option
export const VehicleLookupInput: FC<Props> = ({
  vehicle,
  handleVehicleChange,
  error,
  readOnly,
  autoFocus = false
}) => {
  const navigate = useNavigate()
  const [inputValue, setInputValue] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const { matchingVehicles, loading, errorMessage } =
    useSearchResultsVehicles(inputValue)

  useEffect(() => {
    setIsOpen(inputValue !== '')
  }, [isOpen, inputValue])

  const handleClick = useCallback(
    (option: VehicleEntry) => {
      handleVehicleChange(option)
    },
    [handleVehicleChange]
  )
  const handleCancel = useCallback(() => {
    handleVehicleChange(undefined)
  }, [handleVehicleChange])

  const renderList = (
    _props: React.HTMLAttributes<HTMLLIElement>,
    option: VehicleEntry
  ) =>
    option.id !== '*' ? (
      <li>
        <VehicleListItem
          key={option.id}
          option={option}
          handleClick={handleClick.bind(null, option)}
        />
      </li>
    ) : (
      <Tooltip title={'Need to select customer first'}>
        <span>
          <ListItem
            alignItems="center"
            key={option.id}
            disableGutters
            disablePadding>
            <ListItemButton disabled>
              <ListItemText
                primary="Create new vehicle"
                primaryTypographyProps={{
                  variant: 'button',
                  textAlign: 'center',
                  color: 'primary'
                }}
              />
            </ListItemButton>
          </ListItem>
        </span>
      </Tooltip>
    )

  return (
    <>
      {!vehicle && (
        <Autocomplete
          id="vehicleSearch"
          value={vehicle}
          open={isOpen}
          loading={loading}
          inputValue={inputValue}
          forcePopupIcon={false}
          autoHighlight
          handleHomeEndKeys
          filterOptions={(options) => options}
          onInputChange={(_e: unknown, newValue) =>
            setInputValue(newValue || '')
          }
          renderOption={renderList}
          options={
            errorMessage
              ? []
              : [{ id: '*', info: {} }, ...(matchingVehicles || [])]
          }
          getOptionLabel={(option) => {
            return (
              describeVehicle(option.info) +
              describeLicense(option.info!) +
              option.info!.name
            )
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              label="Search vehicles"
              fullWidth
              autoFocus={autoFocus}
              error={!!errorMessage || error}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'primary.main' }} />
                  </InputAdornment>
                )
              }}
            />
          )}
          noOptionsText={<NoOptionsText errorMessage={errorMessage} />}
          loadingText={<ListItemLoading />}
        />
      )}
      {vehicle && (
        <ClickableDeletableCard
          label={
            <Stack>
              <Typography noWrap>{describeVehicle(vehicle.info!)}</Typography>
              <Typography variant="body2" noWrap>
                {vehicle.info!.licensePlate || 'N/A'}
              </Typography>
            </Stack>
          }
          onDelete={readOnly ? undefined : handleCancel}
          onClick={() => navigate(AppRoutes.VEHICLES + '/' + vehicle.id)}
          aria-label="vehicleCard"
        />
      )}
    </>
  )
}
